/**
 * Les titres proposés sous la boîte de recherche (docs/11 §7).
 *
 * **Des liens, pas un menu.** Le motif attendu serait une liste déroulante en
 * `role="combobox"` ; il obligerait à reprendre au clavier ce que le navigateur
 * fait déjà — le parcours, le focus, l'annonce — et à retirer aux propositions
 * leur qualité de liens. Or ce sont bien des liens : ils mènent chacun à une
 * page. Rendus en ancres dans le flux, « ouvrir dans un nouvel onglet », le clic
 * du milieu et l'annonce « lien » survivent, et il n'y a aucune touche à
 * réimplémenter. C'est la leçon de l'interstitiel de la messagerie, sur un autre
 * objet : un `<button>` déguisé supprime trois comportements pour en simuler un.
 *
 * La liste est **dans le flux** et non en surimpression : superposée, elle
 * masquerait les filtres justes en dessous, et un lecteur de petite taille
 * n'aurait aucun moyen de les atteindre sans vider son champ.
 */
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import type { CourseSuggestion } from "@/features/catalog/api/useCatalog";

interface CourseSuggestionsProps {
  suggestions: CourseSuggestion[];
}

export function CourseSuggestions({ suggestions }: CourseSuggestionsProps) {
  const { t } = useTranslation("catalog");

  if (suggestions.length === 0) {
    return null;
  }

  return (
    <div className="mt-2">
      {/* `role="status"` annonce le nombre sans voler le focus : l'utilisateur
          tape encore, et déplacer le curseur au premier résultat lui ferait
          perdre sa saisie. */}
      <p role="status" className="sr-only">
        {t("catalog.suggestionCount", { count: suggestions.length })}
      </p>
      <ul
        aria-label={t("catalog.suggestions")}
        className="divide-y divide-slate-200 rounded-md border border-slate-200 dark:divide-slate-700 dark:border-slate-700"
      >
        {suggestions.map((suggestion) => (
          <li key={suggestion.id}>
            <Link
              to="/courses/$slug"
              params={{ slug: suggestion.slug }}
              className="block px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
            >
              {suggestion.title}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
